import { CapsuleType } from "app/types";
import { AnanseApiService } from "./AnanseApiService";

type UpdateFailure = {
  capsuleId: number;
  serialNumber: string;
};

export type ExtendCapsulesResult = {
  total: number;
  updated: number;
  failures: UpdateFailure[];
};

type ProgressCallback = (updated: number, total: number) => void;

export class CapsuleDueDateService {
  private readonly api = new AnanseApiService();

  async extendActiveCapsules(
    deviceId: number | string,
    days: number,
    remainingShots?: number,
    onProgress?: ProgressCallback,
  ): Promise<ExtendCapsulesResult> {
    if (!Number.isInteger(days) || days <= 0) {
      throw new Error("A quantidade de dias deve ser um número inteiro positivo.");
    }
    if (
      remainingShots !== undefined &&
      (!Number.isInteger(remainingShots) || remainingShots < 0)
    ) {
      throw new Error(
        "A quantidade de disparos deve ser um número inteiro igual ou maior que zero.",
      );
    }

    const [settings, deviceCapsules] = await Promise.all([
      this.api.makeHttpRequest({ method: "GET", url: "/settings" }),
      this.api.makeHttpRequest({
        method: "GET",
        url: `/capsules/device/${deviceId}`,
      }),
    ]);

    const activeCapsules = ((deviceCapsules || []) as CapsuleType[]).filter(
      capsule => capsule.deletedAt === null,
    );
    const failures: UpdateFailure[] = [];
    let updated = 0;

    onProgress?.(0, activeCapsules.length);

    // Temporário: mantém as chamadas sequenciais até existir o endpoint em lote.
    for (const capsule of activeCapsules) {
      try {
        const currentDueDate = new Date(capsule.dueDate);
        if (Number.isNaN(currentDueDate.getTime())) {
          throw new Error("Data de validade inválida");
        }

        currentDueDate.setUTCDate(currentDueDate.getUTCDate() + days);

        await this.api.makeHttpRequest({
          method: "PUT",
          url: `/capsules/notremainingshots/${capsule.capsuleId}`,
          data: {
            customerCode: settings.customerCode,
            capsuleId: capsule.capsuleId,
            deviceId: capsule.deviceId,
            fragranceId: capsule.fragranceId,
            dueDate: currentDueDate.toISOString(),
            serialNumber: capsule.serialNumber,
            remainingShots: remainingShots ?? capsule.remainingShots,
            performedShots: 1,
            deletedAt: capsule.deletedAt,
          },
        });
        updated += 1;
      } catch (error) {
        failures.push({
          capsuleId: capsule.capsuleId,
          serialNumber: capsule.serialNumber,
        });
      } finally {
        onProgress?.(updated + failures.length, activeCapsules.length);
      }
    }

    return {
      total: activeCapsules.length,
      updated,
      failures,
    };
  }
}
